import React, { useState } from "react";
import classNames from "classnames";
import { ButtonEdit } from "../Button";
import FormSelect from "../Form/FormSelect";
import { isEmpty } from "lodash";
import TooltipWrapper from "../TooltipWrapper";

/**
 * EditableSelectField - A reusable component for toggling between edit and display modes for selection fields
 * Specifically designed for dropdown/select components that can be edited inline
 * Manages its own edit state internally and displays selected option label
 *
 * @param {string} fieldName - The field name for the edit button id and FormSelect name
 * @param {boolean} disabled - Whether the selection editing is disabled
 * @param {any} value - The current selected value (used to check if display should be shown)
 * @param {string} className - Additional CSS classes for the container
 * @param {string} displayClassName - Custom CSS classes for the display text
 * @param {Object} control - React Hook Form control object for FormSelect
 * @param {Array} options - Options array for the FormSelect dropdown
 * @param {string} placeholder - Placeholder text for the FormSelect
 * @param {Function} onChangeExtra - Additional onChange handler for FormSelect
 * @param {boolean} initialEditMode - Initial edit mode state (default: false)
 * @param {Function} getDisplayText - Function to get display text from selected value (optional, defaults to finding label from options)
 * @param {boolean} isEditing - External control for editing state (optional, overrides internal state when provided)
 * @param {Function} onToggleEdit - Callback when edit mode is toggled (optional, used with external isEditing prop)
 */
const EditableSelectField = ({
  fieldName,
  disabled = false,
  value,
  className = "",
  displayClassName = "",
  control,
  options = [],
  placeholder = "Select option",
  onChangeExtra,
  initialEditMode = false,
  getDisplayText,
  isEditing: externalIsEditing,
  onToggleEdit,
}) => {
  const [internalIsEditing, setInternalIsEditing] = useState(initialEditMode);

  const isEditing = externalIsEditing !== undefined ? externalIsEditing : internalIsEditing;

  const handleToggleEdit = () => {
    if (externalIsEditing !== undefined) {
      onToggleEdit?.();
      return;
    }

    setInternalIsEditing(!internalIsEditing);
  };

  const getSelectedOptionLabel = (selectedValue) => {
    if (getDisplayText) {
      return getDisplayText(selectedValue);
    }
    const selectedOption = options.find((option) => option.value === selectedValue);
    return selectedOption ? selectedOption.label : "";
  };

  const displayText = getSelectedOptionLabel(value);

  return (
    <div className={classNames("d-flex align-items-center", className)}>
      <div
        className={classNames("flex-grow-1", {
          "d-none": !isEditing,
          "d-block": isEditing,
        })}
      >
        <FormSelect
          name={fieldName}
          control={control}
          placeholder={placeholder}
          options={options}
          classGroup="mb-0"
          onChangeExtra={onChangeExtra}
          disabled={disabled}
        />
      </div>

      <div
        className={classNames("flex-grow-1 d-flex align-items-center gap-1", {
          "d-none": isEditing,
          "d-block": !isEditing,
        })}
      >
        {!isEmpty(value) && (
          <>
            <TooltipWrapper
              id="tooltip-deploy-instance-test-package-name"
              tooltipText={displayText || ""}
              checkOverflow={true}
              tooltipClassName="max-width-by-px-300"
            >
              <p className={`mb-0 important-max-width-by-px-200 min-width-by-px-80 overflow-hidden text-truncate ${displayClassName}`}>
                {displayText || ""}
              </p>
            </TooltipWrapper>
          </>
        )}
        <ButtonEdit isShow={!isEditing && !disabled} handleClick={handleToggleEdit} id={fieldName} />
      </div>
    </div>
  );
};

export default EditableSelectField;
